import { InPageEditableSectionField } from "components/MainSectionTemplate/types";
import React from "react";
import { DaysAfterDateInput, DaysAfterDateText } from "./DaysAfterDateRow";
import { PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import { PlusButton } from "components/Atoms/PlusMinusButton";


// Enforces that instruction[prop] is of type `number[]`
interface Props<K extends keyof server.dto.UpdateClientConfigurationInstruction> {
  instruction: server.dto.UpdateClientConfigurationInstruction
  reminderIntervalProp: K extends any ? server.dto.UpdateClientConfigurationInstruction[K] extends number[] ? K : never : never
  isHidden: boolean;
  keyPrefix: string;
  onTrashClick: () => void;
  onAddReminderClick: () => void;
  maxReminders: number;
  maxReminderInterval: number;
}

export const getReminderFields = <K extends keyof server.dto.UpdateClientConfigurationInstruction>(props: Props<K>): InPageEditableSectionField[] => {
  const currentReminderIntervals = props.instruction[props.reminderIntervalProp] ?? [];

  // Gives us [1, 2, ..., numberOfReminders]
  const reminders = Array.from(
    { length: currentReminderIntervals.length },
    (_, i) => i + 1
  );

  // We can add reminders if:
  // - We haven't reached the max number of reminders  AND
  // - The last reminder interval is less than the max allowed interval
  // Otherwise if the max is 90 days, and the last reminder is already at 90 days, the next reminder would be 97 days which is invalid
  const canAddReminders = currentReminderIntervals.length < props.maxReminders && ((currentReminderIntervals[currentReminderIntervals.length - 1] ?? 0) < (props.maxReminderInterval - 7));

  const isLastReminder = (index: number) => index === reminders.length - 1;

  return [...reminders.map((_, idx) => ({
    key: `${props.keyPrefix}_reminder_${idx + 1}`,
    label: `${RESX.Reminder.xReminder} ${idx + 1}`,
    render: () => <div className="flex">
      <div className="grow1">
        <DaysAfterDateText days={currentReminderIntervals[idx]} type="due" />
      </div>
      {isLastReminder(idx) && <div>
        <PhosphorTrashButton
          layout="in-square"
          onClick={props.onTrashClick}
        />
      </div>}
    </div>,
    hidden: props.isHidden,
    inPageEdit: {
      render: ({ onChange, value, cancel }) => <DaysAfterDateInput
        value={value}
        onChange={onChange}
        maxValue={props.maxReminderInterval}
        type="due"
        onEnter={v => onChange(v, true)}
        onEsc={cancel}
      />,
      type: "confirmOrCancelButton" as const,
      initialValue: currentReminderIntervals[idx],
      submitChange: (value: number) => {
        const newIntervals = [...currentReminderIntervals];
        newIntervals[idx] = value;
        return window.CoreApi.ClientConfiguration.Update({ ...props.instruction, [props.reminderIntervalProp]: newIntervals });
      }
    }
  })),
  {
    key: `${props.keyPrefix}_add_reminder`,
    label: "",
    hideLabel: false,
    hidden: props.isHidden || !canAddReminders,
    render: () => <PlusButton onClick={props.onAddReminderClick} />
  }]
}